import { useEffect, useRef } from "react";

type ParsedShortcut = {
	key: string;
	mod: boolean;
	shift: boolean;
	alt: boolean;
};

/**
 * Parses a shortcut string like "mod+shift+k" into its parts.
 * "mod" matches Cmd on Mac and Ctrl elsewhere (either one triggers).
 */
export const parseShortcut = (shortcut: string): ParsedShortcut => {
	const parts = shortcut
		.toLowerCase()
		.split("+")
		.map((part) => part.trim());
	return {
		key: parts[parts.length - 1],
		mod:
			parts.includes("mod") ||
			parts.includes("cmd") ||
			parts.includes("ctrl"),
		shift: parts.includes("shift"),
		alt: parts.includes("alt"),
	};
};

const isEditableTarget = (target: EventTarget | null): boolean => {
	if (!(target instanceof HTMLElement)) return false;
	return (
		target.isContentEditable ||
		target instanceof HTMLInputElement ||
		target instanceof HTMLTextAreaElement ||
		target instanceof HTMLSelectElement
	);
};

export const matchesShortcut = (
	event: KeyboardEvent,
	parsed: ParsedShortcut
): boolean => {
	if (event.key.toLowerCase() !== parsed.key) return false;
	if (parsed.mod !== (event.metaKey || event.ctrlKey)) return false;
	if (parsed.shift !== event.shiftKey) return false;
	if (parsed.alt !== event.altKey) return false;
	// Bare-key shortcuts must not fire while the user is typing.
	if (!parsed.mod && isEditableTarget(event.target)) return false;
	return true;
};

export const isMacPlatform = (): boolean =>
	typeof navigator !== "undefined" &&
	/mac|iphone|ipad|ipod/i.test(navigator.platform || navigator.userAgent);

/**
 * Formats a shortcut string for display, respecting the platform:
 * "mod+shift+k" -> "⌘⇧K" on Mac, "Strg+⇧+K" on Windows/Linux.
 */
export const formatShortcut = (shortcut: string): string => {
	const parsed = parseShortcut(shortcut);
	const mac = isMacPlatform();
	const key =
		parsed.key.length === 1
			? parsed.key.toUpperCase()
			: parsed.key.charAt(0).toUpperCase() + parsed.key.slice(1);
	const parts = [
		...(parsed.mod ? [mac ? "⌘" : "Strg"] : []),
		...(parsed.alt ? [mac ? "⌥" : "Alt"] : []),
		...(parsed.shift ? ["⇧"] : []),
		key,
	];
	return parts.join(mac ? "" : "+");
};

/**
 * Formats a shortcut for the aria-keyshortcuts attribute, e.g.
 * "mod+shift+k" -> "Meta+Shift+K Control+Shift+K" (mod matches either).
 */
export const ariaKeyShortcuts = (shortcut: string): string => {
	const parsed = parseShortcut(shortcut);
	const key =
		parsed.key.length === 1
			? parsed.key.toUpperCase()
			: parsed.key.charAt(0).toUpperCase() + parsed.key.slice(1);
	const modifiers = [
		...(parsed.alt ? ["Alt"] : []),
		...(parsed.shift ? ["Shift"] : []),
	];
	const combo = (lead: string[]) => [...lead, ...modifiers, key].join("+");
	return parsed.mod ? `${combo(["Meta"])} ${combo(["Control"])}` : combo([]);
};

/**
 * useKeyboardShortcut - Triggers the handler when the given shortcut is
 * pressed anywhere on the page. Pass undefined to disable the listener.
 * @example useKeyboardShortcut("mod+shift+k", () => inputRef.current?.focus());
 */
export const useKeyboardShortcut = (
	shortcut: string | undefined,
	handler: (event: KeyboardEvent) => void
) => {
	// Keep the latest handler in a ref so the document listener is only
	// re-registered when the shortcut itself changes.
	const handlerRef = useRef(handler);
	useEffect(() => {
		handlerRef.current = handler;
	});

	useEffect(() => {
		if (!shortcut) return;
		const parsed = parseShortcut(shortcut);
		const listener = (event: KeyboardEvent) => {
			if (!matchesShortcut(event, parsed)) return;
			// Prevent even repeated keydowns so a held shortcut never leaks
			// to the browser (e.g. Firefox opens bookmarks on held Ctrl+D).
			event.preventDefault();
			if (event.repeat) return;
			handlerRef.current(event);
		};
		document.addEventListener("keydown", listener);
		return () => document.removeEventListener("keydown", listener);
	}, [shortcut]);
};
